import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './App.css';
import './styles/Accessibility.css';
import './styles/Print.css';
import { BookingProvider } from './pages/BookingContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import ScrollToTopButton from './components/ScrollToTopButton';
import Breadcrumb from './components/Breadcrumb';
import Home from './pages/Home';
import ThankYou from './pages/ThankYou';
import './utils/consoleFilter';

const Rooms = lazy(() => import('./pages/Rooms'));
const RoomDetails = lazy(() => import('./pages/RoomDetails'));
const About = lazy(() => import('./pages/About'));
const Booking = lazy(() => import('./pages/Booking'));
const Restaurant = lazy(() => import('./pages/Restaurant'));
const CommunityCenter = lazy(() => import('./components/CommunityCenter'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const BookingPolicy = lazy(() => import('./pages/BookingPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const Developer = lazy(() => import('./pages/Developer'));

const RouteFallback = () => (
  <div className="route-fallback" role="status" aria-live="polite" aria-label="Loading page" />
);

function App() {
  return (
    <BookingProvider>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Damtua Inn Hotel & Restaurant - Best Hotel in Alikadam, Bandarban</title>
        <meta name="title" content="Damtua Inn Hotel & Restaurant - Best Hotel in Alikadam, Bandarban" />
        <meta name="description" content="Experience the best hotel in Alikadam, Bandarban. Luxury accommodation, local cuisine, community center, and stunning hill views. Book your stay at Damtua Inn for an unforgettable experience." />
        <meta name="keywords" content="best hotel in Alikadam, hotels in Alikadam, accommodation Alikadam, Bandarban hotels, hill station hotel, luxury hotel Alikadam, restaurant Alikadam, community center Alikadam, best place to stay Bandarban" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="author" content="Damtua Inn Hotel & Restaurant" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://damtuainn.com/" />
        <meta property="og:title" content="Damtua Inn Hotel & Restaurant - Best Hotel in Alikadam, Bandarban" />
        <meta property="og:description" content="Experience the best hotel in Alikadam, Bandarban. Luxury accommodation, local cuisine, community center, and stunning hill views." />
        <meta property="og:image" content="https://damtuainn.com/og-image.jpg" />
        <meta property="og:site_name" content="Damtua Inn Hotel & Restaurant" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://damtuainn.com/" />
        <meta property="twitter:title" content="Damtua Inn Hotel & Restaurant - Best Hotel in Alikadam, Bandarban" />
        <meta property="twitter:description" content="Experience the best hotel in Alikadam, Bandarban. Luxury accommodation, local cuisine, community center, and stunning hill views." />
        <meta property="twitter:image" content="https://damtuainn.com/og-image.jpg" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content="#008080" />
        <meta name="msapplication-TileColor" content="#008080" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content="Damtua Inn" />
        
        {/* Location and Business Meta Tags */}
        <meta name="geo.region" content="BD-01" />
        <meta name="geo.placename" content="Alikadam, Bandarban, Chittagong Division, Bangladesh" />
        <meta name="geo.position" content="21.6503;92.3060" />
        <meta name="ICBM" content="21.6503, 92.3060" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://damtuainn.com/" />
        
        {/* Advanced Organization Schema */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Damtua Inn Hotel & Restaurant",
            "alternateName": ["The Damtua Inn", "Damtua Inn", "Damtua Hotel"],
            "description": "Premier hotel and restaurant in Alikadam, Bandarban offering luxury accommodation, authentic Bengali cuisine, and community event services in the heart of Bangladesh's hill tracts.",
            "url": "https://damtuainn.com",
            "logo": {
              "@type": "ImageObject",
              "url": "https://damtuainn.com/logo.png",
              "width": 512,
              "height": 512
            },
            "image": [
              "https://damtuainn.com/images/restaurant-1.webp",
              "https://damtuainn.com/images/rooms/ac-couple-1.webp",
              "https://damtuainn.com/images/community-1.webp"
            ],
            "sameAs": [
              "https://www.facebook.com/profile.php?id=100057598592990"
            ],
            "contactPoint": [
              {
                "@type": "ContactPoint",
                "telephone": "+8801879917302",
                "contactType": "customer service",
                "areaServed": "BD",
                "availableLanguage": ["English", "Bengali"],
                "contactOption": "TollFree"
              },
              {
                "@type": "ContactPoint",
                "telephone": "+8801748912127",
                "contactType": "reservations",
                "areaServed": "BD",
                "availableLanguage": ["English", "Bengali"],
                "serviceType": "Hotel Booking"
              },
              {
                "@type": "ContactPoint",
                "telephone": "+8801883535200",
                "contactType": "customer service",
                "areaServed": "BD",
                "availableLanguage": ["English", "Bengali"],
                "serviceType": "Restaurant"
              }
            ],
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Damtua, Alikadam",
              "addressLocality": "Alikadam",
              "addressRegion": "Bandarban",
              "postalCode": "4650",
              "addressCountry": "BD"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 21.6503,
              "longitude": 92.3060
            },
            "foundingDate": "2020",
            "numberOfEmployees": {
              "@type": "QuantitativeValue",
              "value": 15
            },
            "knowsAbout": [
              "Hotel Management",
              "Bengali Cuisine",
              "Hill Station Tourism",
              "Event Management",
              "Local Culture",
              "Eco-Tourism"
            ],
            "memberOf": {
              "@type": "Organization",
              "name": "Bangladesh Hotel Association"
            },
            "award": [
              "Best Local Hotel in Alikadam 2023",
              "Authentic Bengali Cuisine Award 2023"
            ],
            "slogan": "Your peaceful escape in the hills of Alikadam, Bandarban"
          }
          `}
        </script>
        
        {/* WebSite Schema with Search Action */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Damtua Inn Hotel & Restaurant",
            "alternateName": "The Damtua Inn",
            "url": "https://damtuainn.com",
            "description": "Official website of Damtua Inn Hotel & Restaurant - Best hotel in Alikadam, Bandarban",
            "inLanguage": ["en", "bn"],
            "isAccessibleForFree": true,
            "isFamilyFriendly": true,
            "keywords": "hotel, restaurant, Alikadam, Bandarban, accommodation, Bengali food, hill station, tourism",
            "about": {
              "@type": "Thing",
              "name": "Hotel and Restaurant Services in Bandarban"
            },
            "author": {
              "@type": "Organization",
              "name": "Damtua Inn Hotel & Restaurant"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Damtua Inn Hotel & Restaurant",
              "logo": {
                "@type": "ImageObject",
                "url": "https://damtuainn.com/logo.png"
              }
            },
            "potentialAction": {
              "@type": "SearchAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": "https://damtuainn.com/rooms?search={search_term_string}"
              },
              "query-input": "required name=search_term_string"
            },
            "mainEntity": {
              "@type": "Hotel",
              "name": "Damtua Inn Hotel & Restaurant",
              "telephone": "+8801879917302",
              "priceRange": "$$",
              "image": "https://damtuainn.com/images/restaurant-1.webp",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Damtua, Alikadam",
                "addressLocality": "Alikadam",
                "addressRegion": "Bandarban",
                "postalCode": "4650",
                "addressCountry": "BD"
              }
            }
          }
          `}
        </script>
        
        {/* Enhanced Structured Data for Hotel */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "Hotel",
            "name": "Damtua Inn Hotel & Restaurant",
            "alternateName": "The Damtua Inn",
            "description": "Best hotel in Alikadam, Bandarban offering luxury accommodation, local cuisine, community center, and stunning hill views. Experience authentic hill station hospitality.",
            "url": "https://damtuainn.com/",
            "logo": "https://damtuainn.com/logo.png",
            "image": [
              "https://damtuainn.com/og-image.jpg",
              "https://damtuainn.com/images/restaurant-1.webp",
              "https://damtuainn.com/images/rooms/ac-couple-1.webp"
            ],
            "telephone": "+8801879917302",
            "email": "thedamtuainn@gmail.com",
            "priceRange": "$$",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Damtua, Alikadam",
              "addressLocality": "Alikadam",
              "addressRegion": "Bandarban",
              "addressCountry": "BD",
              "postalCode": "4650"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 21.6503,
              "longitude": 92.3060
            },
            "amenityFeature": [
              {
                "@type": "LocationFeatureSpecification",
                "name": "Restaurant",
                "value": true
              },
              {
                "@type": "LocationFeatureSpecification",
                "name": "Community Center",
                "value": true
              },
              {
                "@type": "LocationFeatureSpecification",
                "name": "Hill View",
                "value": true
              },
              {
                "@type": "LocationFeatureSpecification",
                "name": "Air Conditioning",
                "value": true
              },
              {
                "@type": "LocationFeatureSpecification",
                "name": "Free WiFi",
                "value": true
              },
              {
                "@type": "LocationFeatureSpecification",
                "name": "Parking",
                "value": true
              }
            ],
            "starRating": {
              "@type": "Rating",
              "ratingValue": "4.5",
              "bestRating": "5",
              "worstRating": "1"
            },
            "checkinTime": "14:00",
            "checkoutTime": "12:00",
            "petsAllowed": true,
            "smokingAllowed": true,
            "drinkingAllowed": true,
            "numberOfRooms": 8,
            "hasMap": "https://maps.app.goo.gl/SNBGwZmw6UttUV7G8",
            "sameAs": [
              "https://www.facebook.com/thedamtuainn",
              "https://www.instagram.com/thedamtuainn"
            ],
            "openingHours": "Mo-Su 00:00-23:59",
            "paymentAccepted": ["Cash", "Credit Card", "Mobile Payment"],
            "currenciesAccepted": "BDT",
            "areaServed": {
              "@type": "Country",
              "name": "Bangladesh"
            },
            "serviceArea": {
              "@type": "GeoCircle",
              "geoMidpoint": {
                "@type": "GeoCoordinates",
                "latitude": 21.6503,
                "longitude": 92.3060
              },
              "geoRadius": "50000"
            }
          }
          `}
        </script>
        
        {/* BreadcrumbList Schema */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://damtuainn.com/"
              }
            ]
          }
          `}
        </script>
        
        {/* Organization Schema */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Damtua Inn Hotel & Restaurant",
            "url": "https://damtuainn.com/",
            "logo": "https://damtuainn.com/logo.png",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+8801879917302",
              "contactType": "customer service",
              "areaServed": "BD",
              "availableLanguage": ["English", "Bengali"]
            },
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Damtua, Alikadam",
              "addressLocality": "Alikadam",
              "addressRegion": "Bandarban",
              "postalCode": "4650",
              "addressCountry": "BD"
            }
          }
          `}
        </script>
      </Helmet>
      
      <Router>
        <ScrollToTop />
        <a href="#main-content" className="skip-link">Skip to main content</a>
        <Navbar />
        <Breadcrumb />
        <main id="main-content">
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/rooms" element={<Rooms />} />
              <Route path="/room-details/:id" element={<RoomDetails />} />
              <Route path="/about" element={<About />} />
              <Route path="/booking" element={<Booking />} />
              <Route path="/restaurant" element={<Restaurant />} />
              <Route path="/community-center" element={<CommunityCenter />} />
              <Route path="/thank-you" element={<ThankYou />} />
              <Route path="/admin" element={<AdminPanel />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/booking-policy" element={<BookingPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/developer" element={<Developer />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
        <ScrollToTopButton />
      </Router>
    </BookingProvider>
  );
}

export default App;